/**
 * Member token mirror for native-app webviews.
 *
 * The backend identifies members via the httpOnly `member_token` cookie. Inside
 * the Median (App Store / Play Store) wrapper, cookies set by fetch responses
 * are not reliably persisted, so users kept getting the welcome prompt again.
 * We keep a copy of the token in localStorage and send it as `X-Member-Token`
 * on every same-API request; the backend accepts either.
 *
 * Also consumes `#member_token=…` left in the URL by the native Google
 * sign-in redirect (`/api/access/google/native`).
 */
import axios from 'axios';

const KEY = 'vaad_member_token_v1';
const API = process.env.REACT_APP_BACKEND_URL;

export const getMemberToken = () => {
  try { return localStorage.getItem(KEY) || null; } catch { return null; }
};

export const setMemberToken = (token) => {
  try {
    if (token) localStorage.setItem(KEY, token);
    else localStorage.removeItem(KEY);
  } catch { /* noop */ }
};

export const clearMemberToken = () => setMemberToken(null);

export const isMedianApp = () =>
  typeof window !== 'undefined' &&
  (/median|gonative/i.test(navigator.userAgent) || !!(window.median || window.gonative));

const consumeHashToken = () => {
  const m = window.location.hash.match(/member_token=([^&]+)/);
  if (!m) return;
  setMemberToken(decodeURIComponent(m[1]));
  window.__vaadFreshSignin = true;
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
};

const isApiUrl = (url) => {
  if (typeof url !== 'string') return false;
  if (url.startsWith('/api/')) return true;
  return !!API && url.startsWith(`${API}/api/`);
};

if (typeof window !== 'undefined' && !window.__vaadMemberTokenInstalled) {
  window.__vaadMemberTokenInstalled = true;
  consumeHashToken();

  const baseFetch = window.fetch.bind(window);
  window.fetch = function memberFetch(input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const token = isApiUrl(url) ? getMemberToken() : null;
    if (!token) return baseFetch(input, init);
    const headers = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined));
    if (!headers.has('X-Member-Token')) headers.set('X-Member-Token', token);
    return baseFetch(input, { ...(init || {}), headers });
  };

  axios.interceptors.request.use((config) => {
    const token = isApiUrl(config.url) ? getMemberToken() : null;
    if (token) config.headers['X-Member-Token'] = token;
    return config;
  });
}

export default null;
